import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Package, Search, Plus, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRoofTypes } from '@/hooks/useRoofTypes';
const CATEGORY_LABELS = {
  sheet: 'Sheet', tile: 'Tiles', ridge: 'Ridge', valley: 'Valley', barge: 'Barge',
  spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing', cleaning: 'Cleaning',
  recoat: 'Recoat', aquaseal: 'Dektite', safety: 'Safety', travel: 'Travel',
  urgent: 'Urgent', misc: 'Misc', underlay: 'Underlay',
};

export default function ProductRoofTypesPage() {
  const { api } = useAuth();
  const ROOF_TYPES = useRoofTypes(api);
  const [activeRoofType, setActiveRoofType] = useState('Iron');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addCategory, setAddCategory] = useState('');
  const [saving, setSaving] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 50;

  // Load products assigned to active roof type
  const loadAssigned = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/products?page=${page}&limit=${limit}&search=${search}`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      const { data } = await api.get(url);
      // Filter to only those assigned to this roof type
      const assigned = data.items.filter(p => (p.roof_types || []).includes(activeRoofType));
      setProducts(assigned);
      setAllProducts(data.items);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, activeRoofType, search, categoryFilter, page]);

  // Load counts per roof type
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/products?page=1&limit=1');
      // Get counts per roof type via a quick check
      const countMap = {};
      for (const rt of ROOF_TYPES) {
        // We'll approximate from the page data; for accuracy we'd need a backend endpoint
        countMap[rt] = '...';
      }
      setCounts(countMap);
    } catch {}
  }, [api, ROOF_TYPES]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get('/products/categories');
      setCategories(data);
    } catch {}
  }, [api]);

  useEffect(() => { loadAssigned(); }, [loadAssigned]);
  useEffect(() => { loadCounts(); loadCategories(); }, [loadCounts, loadCategories]);

  const toggleRoofType = async (productId, currentRoofTypes, add) => {
    setSaving(productId);
    try {
      let newTypes;
      if (add) {
        newTypes = [...new Set([...(currentRoofTypes || []), activeRoofType])];
      } else {
        newTypes = (currentRoofTypes || []).filter(rt => rt !== activeRoofType);
      }
      // We need to get the full product to update it
      const { data: product } = await api.get(`/products/${productId}`);
      await api.put(`/products/${productId}`, { ...product, roof_types: newTypes });
      loadAssigned();
    } catch (e) { console.error(e); }
    setSaving(null);
  };

  // Products NOT assigned to this roof type (for the add panel)
  const unassigned = allProducts.filter(p => !(p.roof_types || []).includes(activeRoofType));
  const unassignedFiltered = unassigned.filter(p =>
    (!addSearch || p.name.toLowerCase().includes(addSearch.toLowerCase())) &&
    (!addCategory || p.category === addCategory)
  );

  const bulkAssignCategory = async (cat) => {
    if (!window.confirm(`Assign all "${CATEGORY_LABELS[cat] || cat}" products to ${activeRoofType}?`)) return;
    setSaving('bulk');
    try {
      const { data } = await api.get(`/products?page=1&limit=500&category=${cat}`);
      let updated = 0;
      for (const p of data.items) {
        if (!(p.roof_types || []).includes(activeRoofType)) {
          const newTypes = [...new Set([...(p.roof_types || []), activeRoofType])];
          await api.put(`/products/${p._id}`, { ...p, roof_types: newTypes });
          updated++;
        }
      }
      alert(`Assigned ${updated} ${CATEGORY_LABELS[cat] || cat} products to ${activeRoofType}`);
      loadAssigned();
    } catch (e) { alert('Failed'); }
    setSaving(null);
  };

  const bulkRemoveCategory = async (cat) => {
    if (!window.confirm(`Remove all "${CATEGORY_LABELS[cat] || cat}" products from ${activeRoofType}?`)) return;
    setSaving('bulk');
    try {
      const { data } = await api.get(`/products?page=1&limit=500&category=${cat}`);
      let updated = 0;
      for (const p of data.items) {
        if ((p.roof_types || []).includes(activeRoofType)) {
          const newTypes = (p.roof_types || []).filter(rt => rt !== activeRoofType);
          await api.put(`/products/${p._id}`, { ...p, roof_types: newTypes });
          updated++;
        }
      }
      alert(`Removed ${updated} ${CATEGORY_LABELS[cat] || cat} products from ${activeRoofType}`);
      loadAssigned();
    } catch (e) { alert('Failed'); }
    setSaving(null);
  };

  // PERMANENTLY delete a product category (and every product inside it).
  // Backed by DELETE /api/products/category/{category} which is admin-only.
  // The category vanishes from the dropdown as soon as no product references it.
  const deleteCategory = async (cat) => {
    const label = CATEGORY_LABELS[cat] || cat;
    const cnt = (categories.find(c => c.category === cat) || {}).count || 0;
    if (!window.confirm(`Permanently delete the "${label}" category?\n\nThis removes ALL ${cnt} product${cnt === 1 ? '' : 's'} inside that category from the catalog, from every Roof Type assignment, and from the Materials and Suppliers pages. Cannot be undone.\n\nType-confirm: this DOES delete the category globally — not just for ${activeRoofType}.`)) return;
    setSaving('bulk');
    try {
      const { data } = await api.delete(`/products/category/${encodeURIComponent(cat)}`);
      alert(`Deleted "${label}" — ${data.products_deleted || 0} product${(data.products_deleted || 0) === 1 ? '' : 's'} removed.`);
      // Refresh categories + products so the chip and the assigned list both update.
      await loadCategories();
      await loadAssigned();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete category');
    }
    setSaving(null);
  };

  return (
    <div data-testid="product-roof-types-page" className="space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Product Roof Type Assignments</h1>
        <p className="text-sm text-slate-500 mt-1">Manage which products are available for each roof type. Select a roof type to view and edit assigned products.</p>
      </div>

      <div className="flex gap-5">
        {/* Roof Type Sidebar */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
            {ROOF_TYPES.map(rt => (
              <button
                key={rt}
                data-testid={`rt-select-${rt.replace(/\s/g, '-')}`}
                onClick={() => { setActiveRoofType(rt); setPage(1); setSearch(''); setCategoryFilter(''); setShowAdd(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm border-b border-[#E2E2DF] last:border-0 transition-colors ${activeRoofType === rt ? 'bg-[#D35400]/5 text-[#D35400] font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                {rt}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#2C2C2C]">{activeRoofType}</h2>
            <div className="flex gap-2">
              <button data-testid="show-add-panel" onClick={() => setShowAdd(!showAdd)} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${showAdd ? 'bg-slate-200 text-slate-700' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                {showAdd ? <X size={14} /> : <Plus size={14} />}
                {showAdd ? 'Close' : 'Add Products'}
              </button>
            </div>
          </div>

          {/* Bulk Assign/Delete by Category */}
          <div className="bg-slate-50 border border-[#E2E2DF] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</span>
              <span className="text-[10px] text-slate-400">
                <strong className="text-green-700">+</strong> assigns all category products to <strong>{activeRoofType}</strong> · <strong className="text-rose-600">×</strong> <strong>deletes the whole category</strong>
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.map(c => (
                <div key={c.category} className="inline-flex rounded-md overflow-hidden border border-[#E2E2DF]" title={`${c.count} products in ${CATEGORY_LABELS[c.category] || c.category}`}>
                  <button
                    data-testid={`bulk-assign-${c.category}`}
                    onClick={() => bulkAssignCategory(c.category)}
                    disabled={saving === 'bulk'}
                    className="text-xs px-2 py-1 bg-white text-green-600 hover:bg-green-50 border-r border-[#E2E2DF] disabled:opacity-50"
                    title={`Assign all ${CATEGORY_LABELS[c.category] || c.category} (${c.count}) products to ${activeRoofType}`}
                  >
                    + {CATEGORY_LABELS[c.category] || c.category}
                    <span className="ml-1 text-[10px] text-slate-400">{c.count}</span>
                  </button>
                  <button
                    data-testid={`delete-category-${c.category}`}
                    onClick={() => deleteCategory(c.category)}
                    disabled={saving === 'bulk'}
                    className="text-xs px-1.5 py-1 bg-white text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                    title={`Delete the entire "${CATEGORY_LABELS[c.category] || c.category}" category globally`}
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
              {categories.length === 0 && <span className="text-xs text-slate-400 italic">No categories defined yet.</span>}
            </div>
          </div>

          {/* Add Products Panel */}
          {showAdd && (
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-green-800">Add Products to {activeRoofType}</h3>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="add-product-search" value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Search unassigned products..." className="w-full pl-8 pr-3 py-1.5 border border-[#E2E2DF] rounded text-sm bg-white" />
                </div>
                <select value={addCategory} onChange={e => setAddCategory(e.target.value)} className="text-sm border border-[#E2E2DF] rounded px-2 py-1.5 bg-white">
                  <option value="">All Categories</option>
                  {categories.map(c => <option key={c.category} value={c.category}>{CATEGORY_LABELS[c.category] || c.category}</option>)}
                </select>
              </div>
              <div className="max-h-48 overflow-y-auto bg-white border border-[#E2E2DF] rounded-lg">
                {unassignedFiltered.slice(0, 30).map(p => (
                  <div key={p._id} className="flex items-center justify-between px-3 py-2 border-b border-[#E2E2DF] last:border-0 hover:bg-green-50">
                    <div>
                      <span className="text-sm text-slate-700">{p.name}</span>
                      <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{CATEGORY_LABELS[p.category] || p.category}</span>
                    </div>
                    <button
                      data-testid={`assign-product-${p._id}`}
                      onClick={() => toggleRoofType(p._id, p.roof_types, true)}
                      disabled={saving === p._id}
                      className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving === p._id ? '...' : 'Assign'}
                    </button>
                  </div>
                ))}
                {unassignedFiltered.length === 0 && <p className="text-sm text-slate-400 text-center py-4">All visible products are already assigned</p>}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="assigned-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search assigned products..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
            </div>
            <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.category} value={c.category}>{CATEGORY_LABELS[c.category] || c.category}</option>)}
            </select>
          </div>

          {/* Assigned Products List */}
          <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-[#E2E2DF]">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Products assigned to {activeRoofType} — {products.length} shown</span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {products.map(p => (
                <div key={p._id} className="flex items-center justify-between px-4 py-2.5 border-b border-[#E2E2DF] last:border-0 hover:bg-slate-50 group">
                  <div className="flex items-center gap-3 min-w-0">
                    <Check size={14} className="text-green-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm text-slate-800 block truncate">{p.name}</span>
                      <div className="flex gap-1.5 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{CATEGORY_LABELS[p.category] || p.category}</span>
                        {p.supplier && <span className="text-[10px] text-slate-400">{p.supplier}</span>}
                        <span className="text-[10px] text-slate-400">${p.materials_rate?.toFixed(2)} mat | ${p.labour_rate?.toFixed(2)} lab</span>
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-product-${p._id}`}
                    onClick={() => toggleRoofType(p._id, p.roof_types, false)}
                    disabled={saving === p._id}
                    className="text-xs px-2 py-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {saving === p._id ? '...' : 'Remove'}
                  </button>
                </div>
              ))}
              {products.length === 0 && !loading && <p className="px-4 py-8 text-center text-sm text-slate-400">No products assigned to {activeRoofType} matching your filters</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
